import React, { useEffect, useState } from 'react';
import {
  type CookieConsentChoice,
  readCookieConsent,
  writeCookieConsent,
} from './cookieConsentStorage';
import { COOKIE_CONSENT_EN } from './cookieConsentI18n';
import './CookieConsentBanner.css';

export type CookieConsentCopy = {
  title?: string;
  body?: string;
  acceptLabel?: string;
  essentialLabel?: string;
  policyLabel?: string;
  policyHref?: string;
};

export type CookieConsentBannerProps = {
  /**
   * Storage scope — isolate consent per app / public site.
   * Examples: `portal`, `messenger`, `saas:mario-troedel`.
   */
  scope: string;
  /** When false, the banner never mounts (builder preview, etc.). Default true. */
  enabled?: boolean;
  /**
   * Template / editor preview: always show, do not read or write localStorage.
   * Live Freigabe and platform shells leave this unset.
   */
  preview?: boolean;
  copy?: CookieConsentCopy;
  /**
   * The app uses only strictly necessary storage (§ 25 Abs. 2 TDDDG): show a notice with one
   * acknowledge button (`essentialLabel`) instead of a choice that would change nothing.
   */
  essentialOnly?: boolean;
  className?: string;
  onDecision?: (choice: CookieConsentChoice) => void;
};

const DEFAULT_COPY: Required<Omit<CookieConsentCopy, 'policyHref'>> & { policyHref: string } = {
  title: COOKIE_CONSENT_EN.title,
  body: COOKIE_CONSENT_EN.body,
  acceptLabel: COOKIE_CONSENT_EN.accept,
  essentialLabel: COOKIE_CONSENT_EN.essential,
  policyLabel: COOKIE_CONSENT_EN.policy,
  policyHref: '/privacy',
};

/**
 * Fixed bottom cookie / local-storage notice. Decision is persisted in localStorage
 * (survives hard refresh; cleared when the visitor clears site data).
 */
export function CookieConsentBanner({
  scope,
  enabled = true,
  preview = false,
  copy,
  essentialOnly = false,
  className,
  onDecision,
}: CookieConsentBannerProps): React.ReactElement | null {
  const [visible, setVisible] = useState(preview);

  useEffect(() => {
    if (!enabled || !scope) {
      setVisible(false);
      return;
    }
    if (preview) {
      setVisible(true);
      return;
    }
    setVisible(readCookieConsent(scope) == null);
  }, [enabled, scope, preview]);

  if (!enabled || !visible) return null;

  const c = { ...DEFAULT_COPY, ...copy };
  const rootClass = ['cookie-consent', className].filter(Boolean).join(' ');

  const decide = (choice: CookieConsentChoice) => {
    if (!preview) writeCookieConsent(scope, choice);
    setVisible(false);
    onDecision?.(choice);
  };

  return (
    <div className={rootClass} role="dialog" aria-modal="false" aria-labelledby="cookie-consent-title">
      <div className="cookie-consent__panel">
        <h2 id="cookie-consent-title" className="cookie-consent__title">
          {c.title}
        </h2>
        <p className="cookie-consent__body">{c.body}</p>
        <div className="cookie-consent__actions">
          {essentialOnly ? null : (
            <button
              type="button"
              className="cookie-consent__btn cookie-consent__btn--accept"
              onClick={() => decide('accepted')}
            >
              {c.acceptLabel}
            </button>
          )}
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--essential"
            onClick={() => decide('essential')}
          >
            {c.essentialLabel}
          </button>
          {c.policyHref ? (
            <a className="cookie-consent__policy" href={c.policyHref}>
              {c.policyLabel}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
